import React, { useEffect, useState, createContext, useContext, Suspense, lazy, useMemo, useCallback } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from 'react-hot-toast';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Lazy load components
const CustomCursor = lazy(() => import("./components/home/CustomCursor"));
const ReachOutPanel = lazy(() => import("./components/shared/ReachOutPanel"));
const FirstSection = lazy(() => import("./components/home/FirstSection"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const DigitalMarketing = lazy(() => import("./pages/Digital_marketing"));
const Consultancy = lazy(() => import("./pages/Consultancy"));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) {
      window.__lenis.scrollTo(0, { immediate: true });
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

gsap.registerPlugin(ScrollTrigger);

export const ReachOutContext = createContext();
export const useReachOut = () => useContext(ReachOutContext);

function App() {
  const [isReachOutOpen, setIsReachOutOpen] = useState(false);
  const getShouldShowCustomCursor = () => {
    if (typeof window === 'undefined') return true;
    const isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    return !isCoarsePointer && window.innerWidth > 1024;
  };
  const [showCustomCursor, setShowCustomCursor] = useState(getShouldShowCustomCursor);

  const openReachOut = useCallback(() => setIsReachOutOpen(true), []);
  const closeReachOut = useCallback(() => setIsReachOutOpen(false), []);

  const reachOutValue = useMemo(() => ({
    openReachOut,
    closeReachOut,
    isReachOutOpen
  }), [isReachOutOpen, openReachOut, closeReachOut]);

  useEffect(() => {
    let lenis = null;
    let rafId = null;
    
    // Dynamically import and initialize Lenis
    import('lenis').then((LenisModule) => {
      const LenisConstructor = LenisModule.default;
      
      const isMobile = window.matchMedia('(pointer: coarse)').matches;
      const isLowEnd = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4;
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      
      lenis = new LenisConstructor({
        lerp: prefersReducedMotion ? 0.2 : (isMobile ? 0.12 : (isLowEnd ? 0.1 : 0.08)),
        wheelMultiplier: prefersReducedMotion ? 1 : 0.8,
        gestureOrientation: 'vertical',
        normalizeWheel: true,
        smoothWheel: !prefersReducedMotion,
        autoResize: true,
      });

      window.__lenis = lenis;

      const lenisRaf = (time) => {
        lenis.raf(time * 1000);
      };
      
      gsap.ticker.add(lenisRaf);
      gsap.ticker.lagSmoothing(0);

      lenis.on('scroll', ScrollTrigger.update);

      // Refresh ScrollTrigger after content loads
      const refreshTimer = setTimeout(() => {
        ScrollTrigger.refresh();
      }, 100);

      const handleLoad = () => {
        ScrollTrigger.refresh();
      };
      window.addEventListener('load', handleLoad);

      rafId = { lenisRaf, refreshTimer, handleLoad };
    });

    return () => {
      if (rafId) {
        clearTimeout(rafId.refreshTimer);
        window.removeEventListener('load', rafId.handleLoad);
        if (lenis) {
          gsap.ticker.remove(rafId.lenisRaf);
          lenis.off('scroll', ScrollTrigger.update);
          lenis.destroy();
        }
      }
      window.__lenis = null;
    };
  }, []);

  // Sync scrolling state with ReachOutPanel openness
  useEffect(() => {
    if (window.__lenis) {
      if (isReachOutOpen) {
        window.__lenis.stop();
        document.body.style.overflow = 'hidden';
      } else {
        window.__lenis.start();
        document.body.style.overflow = '';
      }
    }
  }, [isReachOutOpen]);

  useEffect(() => {
    const onResize = () => setShowCustomCursor(getShouldShowCustomCursor());
    window.addEventListener('resize', onResize, { passive: true });
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Minimal loading fallback for Suspense
  const SimpleLoader = () => (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#080808]">
      <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
    </div>
  );

  return (
    <HelmetProvider>
      <ReachOutContext.Provider value={reachOutValue}>
        <ScrollToTop />
        <Suspense fallback={null}>
          {showCustomCursor && <CustomCursor />}
        </Suspense>
        <Suspense fallback={<SimpleLoader />}>
          <Routes>
            <Route path="/" element={<FirstSection />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/digital-marketing" element={<DigitalMarketing />} />
            <Route path="/consultancy" element={<Consultancy />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>

        <Suspense fallback={null}>
          <ReachOutPanel isOpen={isReachOutOpen} onClose={closeReachOut} />
        </Suspense>
        
        <Toaster
          position="bottom-right"
          toastOptions={{
            duration: 5000,
            style: {
              background: '#1e1f1e',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '16px',
              padding: '14px 18px',
              fontSize: '14px',
              fontWeight: '500',
              backdropFilter: 'blur(12px)',
              boxShadow: '0 20px 60px -10px rgba(0,0,0,0.7)',
            },
            success: {
              iconTheme: { primary: '#4ade80', secondary: '#1e1f1e' },
            },
            error: {
              iconTheme: { primary: '#f87171', secondary: '#1e1f1e' },
            },
          }}
        />
      </ReachOutContext.Provider>
    </HelmetProvider>
  );
}

export default App;